import * as AzureSpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { type AudioConfig as AudioConfigType } from 'microsoft-cognitiveservices-speech-sdk';
import type * as valibot from 'valibot';
import {
  parse,
  type InferOutput,
  pipe,
  number,
  maxValue,
  minValue,
  array,
  string,
  transform,
  enum_,
  boolean,
  optional,
} from 'valibot';

declare const credentialsSchema: valibot.SchemaWithPipe<
  readonly [
    valibot.IntersectSchema<
      [
        valibot.UnionSchema<
          [
            valibot.ObjectSchema<
              {
                readonly authorizationToken: valibot.StringSchema<undefined>;
                readonly subscriptionKey: valibot.OptionalSchema<
                  valibot.UndefinedSchema<'"subscriptionKey" must be unset when "authorizationToken" is set.'>,
                  undefined
                >;
              },
              undefined
            >,
            valibot.ObjectSchema<
              {
                readonly authorizationToken: valibot.OptionalSchema<
                  valibot.UndefinedSchema<'"authorizationToken" must be unset when "subscriptionKey" is set.'>,
                  undefined
                >;
                readonly subscriptionKey: valibot.StringSchema<undefined>;
              },
              undefined
            >,
          ],
          'The object must either have either "authorizationToken" or "subscriptionKey" set, but not both.'
        >,
        valibot.UnionSchema<
          [
            valibot.ObjectSchema<
              {
                readonly customVoiceHostname: valibot.OptionalSchema<
                  valibot.UndefinedSchema<'"customVoiceHostname" must be unest when "region" is set.'>,
                  undefined
                >;
                readonly region: valibot.StringSchema<undefined>;
                readonly speechRecognitionHostname: valibot.OptionalSchema<
                  valibot.UndefinedSchema<'"speechRecognitionHostname" must be unest when "region" is set.'>,
                  undefined
                >;
                readonly speechSynthesisHostname: valibot.OptionalSchema<
                  valibot.UndefinedSchema<'"speechSynthesisHostname" must be unest when "region" is set.'>,
                  undefined
                >;
              },
              undefined
            >,
            valibot.ObjectSchema<
              {
                readonly customVoiceHostname: valibot.OptionalSchema<
                  valibot.UnionSchema<
                    [valibot.StringSchema<undefined>, valibot.UndefinedSchema<undefined>],
                    undefined
                  >,
                  undefined
                >;
                readonly region: valibot.OptionalSchema<
                  valibot.UndefinedSchema<'"region" must be unset when "*Hostname" is set.'>,
                  undefined
                >;
                readonly speechRecognitionHostname: valibot.StringSchema<undefined>;
                readonly speechSynthesisHostname: valibot.StringSchema<undefined>;
              },
              undefined
            >,
          ],
          'The object must either have either "region" or "*Hostname" set, but not both.'
        >,
      ],
      undefined
    >,
    valibot.ReadonlyAction<
      (
        | {
            authorizationToken: string;
            subscriptionKey?: undefined;
          }
        | {
            authorizationToken?: undefined;
            subscriptionKey: string;
          }
      ) &
        (
          | {
              customVoiceHostname?: undefined;
              region: string;
              speechRecognitionHostname?: undefined;
              speechSynthesisHostname?: undefined;
            }
          | {
              customVoiceHostname?: string | undefined;
              region?: undefined;
              speechRecognitionHostname: string;
              speechSynthesisHostname: string;
            }
        )
    >,
  ]
>;

type Credentials = InferOutput<typeof credentialsSchema>;

const enableTelemetrySchema = optional(boolean());

const initialSilenceTimeoutSchema = optional(pipe(number(), minValue(1), maxValue(60_000)));

const looseEventsSchema = optional(boolean(), false);

const referenceGrammarsSchema = pipe(
  optional(array(string()), []),
  // any(),
  // array(string()),
  // transform<string[], readonly string[]>(value => (Object.isFrozen(value) ? value : Object.freeze([...value])))
  transform<string[], readonly string[]>((value) =>
    Object.isFrozen(value) ? value : Object.freeze([...value])
  )
);

const textNormalizationSchema = optional(
  enum_({
    display: 'display',
    itn: 'itn',
    lexical: 'lexical',
    maskeditn: 'maskeditn',
  }),
  'display'
);

const speechRecognitionEndpointIdSchema = optional(string());

const AudioConfig = AzureSpeechSDK.AudioConfig;

let shouldWarnOnSubscriptionKey = true;

type PatchOptionsInit = {
  audioConfig?: AudioConfigType | undefined;
  credentials: (() => Credentials | Promise<Credentials>) | Credentials | Promise<Credentials>;
  enableTelemetry?: boolean | undefined;
  initialSilenceTimeout?: number | undefined;
  looseEvent?: boolean | undefined;
  looseEvents?: boolean | undefined;
  referenceGrammars?: readonly string[] | undefined;
  speechRecognitionEndpointId?: string | undefined;
  textNormalization?: 'display' | 'itn' | 'lexical' | 'maskeditn' | undefined;
};

type PatchedOptions = Readonly<{
  audioConfig: AudioConfigType;
  enableTelemetry: boolean | undefined;
  fetchCredentials: () => Promise<Credentials>;
  initialSilenceTimeout: PatchOptionsInit['initialSilenceTimeout'];
  looseEvents: boolean;
  referenceGrammars: readonly string[] | undefined;
  speechRecognitionEndpointId: PatchOptionsInit['speechRecognitionEndpointId'];
  textNormalization: PatchOptionsInit['textNormalization'];
}>;

export default function patchOptions(init: PatchOptionsInit): PatchedOptions {
  const {
    audioConfig,
    credentials,
    enableTelemetry,
    initialSilenceTimeout,
    looseEvent,
    referenceGrammars,
    speechRecognitionEndpointId,
    textNormalization,
  } = init;

  let { looseEvents } = init;

  if (typeof looseEvent !== 'undefined') {
    console.warn('web-speech-cognitive-services: The option "looseEvent" should be named as "looseEvents".');

    looseEvents = looseEvent;
  }

  return Object.freeze({
    audioConfig: audioConfig || AudioConfig.fromDefaultMicrophoneInput(),
    // We set telemetry to true to honor the default telemetry settings of Speech SDK
    // https://github.com/Microsoft/cognitive-services-speech-sdk-js#data--telemetry
    enableTelemetry: parse(enableTelemetrySchema, enableTelemetry),
    fetchCredentials: async () => {
      const parsedCredentials = parse(
        credentialsSchema,
        await resolveFunctionOrReturnValue<Credentials>(credentials)
      );

      if (shouldWarnOnSubscriptionKey && parsedCredentials.subscriptionKey) {
        console.warn(
          'web-speech-cognitive-services: In production environment, subscription key should not be used, authorization token should be used instead.'
        );

        shouldWarnOnSubscriptionKey = false;
      }

      return parsedCredentials;
    },
    initialSilenceTimeout: parse(initialSilenceTimeoutSchema, initialSilenceTimeout),
    looseEvents: parse(looseEventsSchema, looseEvents),
    referenceGrammars: parse(referenceGrammarsSchema, referenceGrammars),
    speechRecognitionEndpointId: parse(speechRecognitionEndpointIdSchema, speechRecognitionEndpointId),
    textNormalization: parse(textNormalizationSchema, textNormalization),
  });
}

function isFunction(value: unknown): value is () => unknown {
  return typeof value === 'function';
}

function resolveFunctionOrReturnValue<T>(fnOrValue: (() => Promise<T> | T) | Promise<T> | T): Promise<T> | T {
  return isFunction(fnOrValue) ? fnOrValue() : fnOrValue;
}
